import {Spinner, NavList} from '@primer/react'

import {HomeIcon, StopwatchIcon, FileCodeIcon, MarkdownIcon} from '@primer/octicons-react'
import styles from './ActionsRunJobsList.module.css'
import {useEffect, useState, useRef, useMemo, memo, useCallback} from 'react'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {StatusIcon} from './StatusIcon'
import {NavigationItem} from './components/NavigationItem'
import {JobStatusFilter, statusOptions, type StatusCounts} from './components/JobStatusFilter'
import type {ActionsRunJobsListProps, JobGroup, CheckRunData, JobStatusFilterValue} from './types'

// Used for pagination. We will fetch job groups in batches of 200
const BATCH_SIZE = 200

// Minimum interval between live update fetches (ms)
// This ensures we process at least one event every DEBOUNCE_INTERVAL_MS under high load
const DEBOUNCE_INTERVAL_MS = 500

// Helper function to check if a job matches the selected filter
function matchesFilter(job: CheckRunData, filter: JobStatusFilterValue): boolean {
  if (filter === 'all') return true
  if (filter === 'queued' || filter === 'in_progress' || filter === 'completed') {
    return job.status === filter
  }
  // Filter by conclusion
  return job.conclusion === filter
}

// Helper to get all jobs from job groups (for status counts)
function getAllJobs(jobGroups: JobGroup[]): CheckRunData[] {
  const jobs: CheckRunData[] = []
  for (const group of jobGroups) {
    if (group.nonNested) {
      jobs.push(...group.nonNested.jobs)
    }
    if (group.nested) {
      jobs.push(...group.nested.jobs)
    }
  }
  return jobs
}

// Helper to count jobs matching a filter value
function countJobsForFilter(jobs: CheckRunData[], filter: JobStatusFilterValue): number {
  if (filter === 'all') return jobs.length
  if (filter === 'queued' || filter === 'in_progress' || filter === 'completed') {
    return jobs.filter(job => job.status === filter).length
  }
  // Filter by conclusion
  return jobs.filter(job => job.conclusion === filter).length
}

export function ActionsRunJobsList({
  summaryHref,
  summarySelected,
  jobGroups: initialJobGroups,
  selectedJobId,
  showRunDetails,
  usageHref,
  usageSelected,
  workflowFileHref,
  workflowFileSelected,
  agenticWorkflowFileHref,
  agenticWorkflowFileSelected,
  usageHotkey,
  workflowFileHotkey,
  viewingCurrent,
  liveUpdateChannel,
  liveUpdateUrl,
  jobGroupsFetchUrl,
}: ActionsRunJobsListProps) {
  const [jobGroups, setJobGroups] = useState<JobGroup[]>(initialJobGroups)
  const [isLoading, setIsLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<JobStatusFilterValue>('all')
  const abortControllerRef = useRef<AbortController | null>(null)
  const liveUpdateAbortControllerRef = useRef<AbortController | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // Refs for debouncing live updates
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastFetchTimeRef = useRef<number>(0)
  const isFetchingRef = useRef<boolean>(false)

  // Wrapper props for WebSocket subscription only (no DOM morphing)
  // We use js-socket-channel to subscribe to the Alive WebSocket channel,
  // but NOT js-updatable-content to prevent HTML replacement.
  // Instead, we handle socket:message events ourselves to update React state.
  const wrapperProps = viewingCurrent
    ? {
        ref: containerRef,
        className: 'js-socket-channel',
        'data-channel': liveUpdateChannel,
        // Note: data-url is intentionally omitted to prevent updatable-content behavior
      }
    : {}

  // Handle live updates via WebSocket - fetch fresh data when a socket message arrives
  // Uses AbortController to cancel in-flight requests when a new update is triggered,
  // preventing race conditions where an older response could overwrite a newer one
  const performFetch = useCallback(async () => {
    if (!liveUpdateUrl) return

    // Cancel any in-flight live update request
    if (liveUpdateAbortControllerRef.current) {
      liveUpdateAbortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    const abortController = new AbortController()
    liveUpdateAbortControllerRef.current = abortController

    isFetchingRef.current = true
    // Set at fetch start (not completion) intentionally - this marks the start of the debounce window.
    // Events during the fetch are coalesced, but the fetch returns current server state anyway,
    // so no data is lost. Setting at completion would allow a burst of fetches after slow requests.
    lastFetchTimeRef.current = Date.now()

    try {
      // Request JSON format for the React component (backend supports both HTML and JSON)
      const response = await verifiedFetch(liveUpdateUrl, {
        headers: {
          Accept: 'application/json',
        },
        signal: abortController.signal,
      })
      if (!response.ok) return

      const data = await response.json()
      // Update job groups from the response
      if (data.jobGroups) {
        setJobGroups(data.jobGroups)
      }
    } catch (error) {
      // Ignore abort errors - these are expected when canceling in-flight requests
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      // Log other errors - live updates are best-effort but we want visibility
      // eslint-disable-next-line no-console
      console.error('Failed to fetch live update:', error)
    } finally {
      isFetchingRef.current = false
    }
  }, [liveUpdateUrl])

  // Debounced live update handler that guarantees at least one fetch every DEBOUNCE_INTERVAL_MS
  // Under high event frequency:
  // - First event triggers immediately (leading edge)
  // - Subsequent events within DEBOUNCE_INTERVAL_MS are debounced
  // - After DEBOUNCE_INTERVAL_MS, the next event triggers immediately again
  const handleLiveUpdate = useCallback(() => {
    if (!liveUpdateUrl) return

    const now = Date.now()
    const timeSinceLastFetch = now - lastFetchTimeRef.current

    // Clear any pending debounced fetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    // If enough time has passed since the last fetch, trigger immediately (leading edge)
    if (timeSinceLastFetch >= DEBOUNCE_INTERVAL_MS && !isFetchingRef.current) {
      performFetch()
    } else {
      // Otherwise, schedule a debounced fetch for when the interval expires
      const delay = Math.max(0, DEBOUNCE_INTERVAL_MS - timeSinceLastFetch)
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null
        // Skip if a fetch is already in progress - this prevents aborting an in-flight
        // request that started after we scheduled this timer
        if (isFetchingRef.current) return
        performFetch()
      }, delay)
    }
  }, [liveUpdateUrl, performFetch])

  // Subscribe to socket:message events for live updates
  useEffect(() => {
    if (!viewingCurrent || !containerRef.current) return

    const container = containerRef.current

    const listener = (event: Event) => {
      // Prevent the default updatable-content behavior from other observers
      // by stopping propagation (our handler runs first since we're on the target)
      event.stopPropagation()
      handleLiveUpdate()
    }

    container.addEventListener('socket:message', listener)
    return () => {
      container.removeEventListener('socket:message', listener)
      // Clear any pending debounced fetch
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      // Cancel any in-flight live update request when unmounting
      if (liveUpdateAbortControllerRef.current) {
        liveUpdateAbortControllerRef.current.abort()
      }
    }
  }, [viewingCurrent, handleLiveUpdate])

  // Filter job groups based on selected status filter
  const filteredJobGroups = useMemo(() => {
    if (statusFilter === 'all') return jobGroups

    return jobGroups
      .map(group => {
        const filteredGroup: JobGroup = {name: group.name}

        // Filter non-nested jobs
        if (group.nonNested) {
          const filteredJobs = group.nonNested.jobs.filter(job => matchesFilter(job, statusFilter))
          if (filteredJobs.length > 0) {
            filteredGroup.nonNested = {jobs: filteredJobs}
          }
        }

        // Filter nested jobs
        if (group.nested) {
          const filteredJobs = group.nested.jobs.filter(job => matchesFilter(job, statusFilter))
          if (filteredJobs.length > 0) {
            filteredGroup.nested = {
              ...group.nested,
              jobs: filteredJobs,
            }
          }
        }

        return filteredGroup
      })
      .filter(group => group.nonNested || group.nested) // Remove empty groups
  }, [jobGroups, statusFilter])

  // Pre-calculate status counts for the filter dropdown
  // This avoids recalculating in JobStatusFilter on every render
  const statusCounts = useMemo((): StatusCounts => {
    const allJobs = getAllJobs(jobGroups)
    const counts: StatusCounts = {} as StatusCounts
    for (const option of statusOptions) {
      counts[option.value] = countJobsForFilter(allJobs, option.value)
    }
    return counts
  }, [jobGroups])

  // Load job groups progressively in batches from the backend
  useEffect(() => {
    // Only enable batch loading if we have a fetch URL
    if (!jobGroupsFetchUrl) return

    // Reset to initial state at the start of each effect
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setJobGroups(initialJobGroups)

    // Create abort controller for this effect instance
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    let currentBatch = 0
    let accumulatedGroups: JobGroup[] = [...initialJobGroups]

    const loadNextBatch = async (): Promise<void> => {
      // Check if we've been aborted
      if (abortController.signal.aborted) return

      setIsLoading(true)

      try {
        const separator = jobGroupsFetchUrl.includes('?') ? '&' : '?'
        const response = await verifiedFetch(
          `${jobGroupsFetchUrl}${separator}batch=${currentBatch}&size=${BATCH_SIZE}`,
          {signal: abortController.signal},
        )

        if (!response.ok) {
          throw new Error('Failed to fetch job groups')
        }

        const data = await response.json()
        const newJobGroups = data.jobGroups || []
        const hasMore = data.hasMore || false

        // Check again after async operation
        if (abortController.signal.aborted) return

        // Append new batch to accumulated groups
        accumulatedGroups = [...accumulatedGroups, ...newJobGroups]
        setJobGroups(accumulatedGroups)
        currentBatch++

        // Continue loading if there are more batches
        if (hasMore) {
          await loadNextBatch()
        } else {
          setIsLoading(false)
        }
      } catch (error) {
        // Ignore abort errors
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    // Start loading from batch 0
    loadNextBatch()

    return () => {
      // Cancel any in-flight requests
      abortController.abort()
    }
  }, [jobGroupsFetchUrl, initialJobGroups])

  return (
    <div {...wrapperProps}>
      <div className={styles.container}>
        <NavList aria-label="Workflow run">
          {/* Summary Item */}
          <NavigationItem
            href={summaryHref}
            ariaCurrent={summarySelected ? 'page' : undefined}
            testSelector="summary-link"
            turbo={false}
            leadingVisual={<HomeIcon />}
          >
            Summary
          </NavigationItem>

          {/* Jobs Header - heading and filter are siblings */}
          <div className={styles.jobsHeader}>
            <h4 className={styles.jobsHeading}>
              {statusFilter === 'all'
                ? 'All jobs'
                : `${statusOptions.find(o => o.value === statusFilter)?.label ?? 'All'} jobs`}
            </h4>
            <JobStatusFilter
              selectedFilter={statusFilter}
              onFilterChange={setStatusFilter}
              statusCounts={statusCounts}
            />
          </div>

          {/* Jobs Group */}
          <NavList.Group>
            {filteredJobGroups.map(group => (
              <JobGroupComponent key={group.name} group={group} selectedJobId={selectedJobId} />
            ))}
            {!isLoading && statusFilter !== 'all' && filteredJobGroups.length === 0 && (
              <NavList.Item>
                <span className="color-fg-muted" role="status" aria-live="polite">
                  There are no jobs with this status
                </span>
              </NavList.Item>
            )}
            {isLoading && (
              <NavList.Item>
                <span className={styles.spinnerContainer}>
                  <Spinner size="small" />
                </span>
              </NavList.Item>
            )}
          </NavList.Group>

          {/* Run Details Group */}
          {showRunDetails && (
            <NavList.Group title="Run details">
              {usageHref && (
                <NavigationItem
                  href={usageHref}
                  ariaCurrent={usageSelected ? 'page' : undefined}
                  testSelector="usage-link"
                  turbo={false}
                  leadingVisual={<StopwatchIcon />}
                >
                  Usage
                </NavigationItem>
              )}
              {workflowFileHref && (
                <NavigationItem
                  href={workflowFileHref}
                  ariaCurrent={workflowFileSelected ? 'page' : undefined}
                  testSelector="workflow-file-link"
                  turbo={false}
                  leadingVisual={<FileCodeIcon />}
                >
                  Workflow file
                </NavigationItem>
              )}
              {agenticWorkflowFileHref && (
                <NavigationItem
                  href={agenticWorkflowFileHref}
                  ariaCurrent={agenticWorkflowFileSelected ? 'page' : undefined}
                  testSelector="agentic-workflow-file-link"
                  turbo={false}
                  leadingVisual={<MarkdownIcon />}
                >
                  Agentic Workflow file
                </NavigationItem>
              )}
            </NavList.Group>
          )}
        </NavList>
      </div>

      {/* Hidden hotkey support */}
      {(usageHref || workflowFileHref) && (
        <div hidden>
          {usageHref && usageHotkey && (
            <a data-hotkey={usageHotkey} href={usageHref}>
              Usage
            </a>
          )}
          {workflowFileHref && workflowFileHotkey && (
            <a data-hotkey={workflowFileHotkey} href={workflowFileHref}>
              Workflow file
            </a>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Memoized sub-components for performance optimization
// These prevent unnecessary re-renders when parent state changes but
// the individual item's data hasn't changed.
// ============================================================================

interface JobItemProps {
  job: CheckRunData
  isSelected: boolean
}

/**
 * Memoized job item component.
 * Only re-renders when job data or selection state changes.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const JobItem = memo(function JobItem({job, isSelected}: JobItemProps) {
  return (
    <NavigationItem
      href={job.href}
      ariaCurrent={isSelected ? 'page' : undefined}
      testSelector="job-link"
      turbo={false}
      showRerunButton={job.showRerunButton}
      rerunDialogId={job.rerunDialogId}
      jobId={job.jobId}
      leadingVisual={<StatusIcon status={job.status} conclusion={job.conclusion} size={16} />}
    >
      {job.displayName}
    </NavigationItem>
  )
})

interface NestedJobGroupProps {
  groupName: string
  nestedGroup: NonNullable<JobGroup['nested']>
  selectedJobId: number | null
}

/**
 * Memoized nested job group component.
 * Contains a collapsible sub-nav with jobs.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const NestedJobGroup = memo(function NestedJobGroup({groupName, nestedGroup, selectedJobId}: NestedJobGroupProps) {
  // Always expand nested sub-items unless everything has successfully passed
  const defaultOpen = !(nestedGroup.status === 'completed' && nestedGroup.conclusion === 'success')

  return (
    <NavList.Item defaultOpen={defaultOpen}>
      <NavList.LeadingVisual>
        <StatusIcon status={nestedGroup.status} conclusion={nestedGroup.conclusion} size={16} />
      </NavList.LeadingVisual>
      {groupName}
      <NavList.SubNav>
        {nestedGroup.jobs.map(job => (
          <JobItem key={job.id} job={job} isSelected={job.id === selectedJobId} />
        ))}
      </NavList.SubNav>
    </NavList.Item>
  )
})

interface JobGroupComponentProps {
  group: JobGroup
  selectedJobId: number | null
}

/**
 * Memoized job group component.
 * Only re-renders when group data or selected job changes.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
const JobGroupComponent = memo(function JobGroupComponent({group, selectedJobId}: JobGroupComponentProps) {
  const hasNested = !!group.nested
  const hasNonNested = !!group.nonNested

  return (
    <>
      {/* Render non-nested jobs first */}
      {hasNonNested &&
        group.nonNested &&
        group.nonNested.jobs.map(job => <JobItem key={job.id} job={job} isSelected={job.id === selectedJobId} />)}

      {/* Render nested group */}
      {hasNested && group.nested && (
        <NestedJobGroup groupName={group.name} nestedGroup={group.nested} selectedJobId={selectedJobId} />
      )}
    </>
  )
})
